import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import FluxiLogo from '../../assets/svg/FluxiLogo.svg';
import { useAuth } from '../../context/AuthContext';
import { membershipStatsAxios } from '../../services/membership/MembershipAxios';
import { createUserBrand, type UserBrand } from '../../services/settings/userBrands';
import { getBrandContext, postCreateHome, CreateHomeData } from '../../services/home/homeService';
import { dictionary, Language } from '../../utils/dictionary';
import Loader from '../common/Loader';
import { ModalWrapper } from '../modals/ModalWrapper';
import { BuyCreditsModal } from '../modals/BuyCreditsModal';
import { useOnboardingStore } from './store/onboarding-store';
import type { StoreTemplateStyleId } from './store/template-design-options';
import { PayloadBrandContext, StepSelectFunnels } from './steps/step-1-select-funnels';
import { StepBrandConfig } from './steps/step-2-brand-config';

export interface PayloadBrandContextComplete {
	websites_info: PayloadBrandContext[];
}

const STORE_V2_TEMPLATE_DESIGN = {
	template_family: 'store-sophisticated-html-v1',
	contract_version: 'sophisticated-html-v2',
	style_sheet: 'premium_fashion',
	theme: 'store_sophisticated_html_v1',
	template_name: 'Store Sophisticated HTML V1',
};

const STEP_TITLES = ['Seleccionar embudos', 'Configurar marca'];

export function OnboardingLayoutHome() {
	const navigate = useNavigate();
	const { language } = useAuth();
	const { data, nextStep, previousStep } = useOnboardingStore();
	const { step, selectedFunnels, productLandingLinks } = data;
	const [payloadBrandContext, setPayloadBrandContext] = useState<PayloadBrandContext[]>([]);
	const [loading, setLoading] = useState(false);
	const [loadingCreateHome, setLoadingCreateHome] = useState(false);
	const [brandName, setBrandName] = useState<string[]>([]);
	const [brandContext, setBrandContext] = useState<string[]>([]);
	const [selectedBrandName, setSelectedBrandName] = useState('');
	const [selectedBrandContext, setSelectedBrandContext] = useState('');
	const [selectedBrandLogoImage, setSelectedBrandLogoImage] = useState<string | undefined>();
	const selectedStyleSheet: StoreTemplateStyleId = 'premium_fashion';
	const [selectedPalette, setSelectedPalette] = useState('auto');
	const [isOpenBuyCreditsModal, setIsOpenBuyCreditsModal] = useState(false);
	const [endMembershipDate, setEndMembershipDate] = useState('');

	useEffect(() => {
		membershipStatsAxios()
			.then((res) => {
				if (res?.active_membership) {
					setEndMembershipDate(res.active_membership.ends_at);
				}
			})
			.catch(() => {});
	}, []);

	const renderStepContent = () => {
		if (step === 2) {
			return (
				<StepBrandConfig
					loading={loading}
					name={brandName}
					context={brandContext}
					selectedBrandName={selectedBrandName}
					selectedBrandContext={selectedBrandContext}
					selectedBrandLogoImage={selectedBrandLogoImage}
					selectedStyleSheet={selectedStyleSheet}
					selectedPalette={selectedPalette}
					setSelectedBrandName={setSelectedBrandName}
					setSelectedBrandContext={setSelectedBrandContext}
					setSelectedBrandLogoImage={setSelectedBrandLogoImage}
					setSelectedPalette={setSelectedPalette}
				/>
			);
		}

		return (
			<StepSelectFunnels
				setPayloadBrandContext={setPayloadBrandContext}
				payloadBrandContext={payloadBrandContext}
			/>
		);
	};

	const handleNextStep = () => {
		if (selectedFunnels.length === 0) {
			toast.error(dictionary['error-select-funnel'][language]);
			return;
		}

		setLoading(true);
		getBrandContext({ websites_info: payloadBrandContext })
			.then((response) => {
				setBrandName(response.brands);
				setBrandContext(response.contexts);
			})
			.catch((error) => {
				console.error(error);
			})
			.finally(() => {
				setLoading(false);
			});

		nextStep();
	};

	const buildStoreBrandContext = (brand?: UserBrand | null) => {
		const finalName = brand?.name || selectedBrandName;
		const context = {
			id: brand?.id,
			brand_id: brand?.id,
			name: finalName,
			brand_name: finalName,
			summary: selectedBrandContext,
			description: selectedBrandContext,
			personality_text: brand?.personality_text || selectedBrandContext,
			brandboard_url: brand?.brandboard_url,
			logo_url: brand?.logo_url,
			logo_dark_url: brand?.logo_dark_url,
			logo_light_url: brand?.logo_light_url,
			brand_colors: brand?.brand_colors,
			font_family: brand?.font_family,
			font_technical_description: brand?.font_technical_description,
		};

		return Object.fromEntries(
			Object.entries(context).filter(([, value]) => value !== undefined && value !== null && value !== '')
		);
	};

	const handleSubmit = async () => {
		if (selectedBrandName.trim() === '' || selectedBrandContext.trim() === '') {
			toast.error(dictionary['error-brand-data'][language] || 'Por favor, completa el nombre de la marca y el contexto');
			return;
		}

		setLoadingCreateHome(true);
		try {
			let createdBrand: UserBrand | null = null;
			if (selectedBrandLogoImage) {
				createdBrand = await createUserBrand({
					name: selectedBrandName.trim(),
					logo_image: selectedBrandLogoImage,
					personality_text: selectedBrandContext,
					brand_colors: {},
					auto_generate_metadata: true,
				});
			}

			const finalBrandName = createdBrand?.name || selectedBrandName;
			const templateDesign = {
				...STORE_V2_TEMPLATE_DESIGN,
				style_sheet: selectedStyleSheet,
				theme: selectedStyleSheet,
				...(selectedPalette !== 'auto' ? { palette: selectedPalette } : {}),
			};
			const payload: CreateHomeData = {
				brand_name: finalBrandName,
				context: selectedBrandContext,
				funnels: selectedFunnels.map((funnel) => funnel.id),
				configurations: {
					enabled: true,
					brand_context: buildStoreBrandContext(createdBrand),
					product_landing_links: productLandingLinks,
				},
				template_design: templateDesign,
			};

			await postCreateHome(payload);
			toast.success(dictionary['success-create-home'][language]);
			setTimeout(() => {
				navigate('/funnels?type=homes');
			}, 1000);
		} catch (error: any) {
			if (error.response?.data?.trigger === 'INSUFFICIENT_CREDITS') {
				setIsOpenBuyCreditsModal(true);
			} else {
				toast.error(dictionary['error-create-home'][language]);
			}
		} finally {
			setLoadingCreateHome(false);
		}
	};

	const isLastStep = step === 2;
	const isBrandDataEmpty = selectedBrandName.trim() === '' || selectedBrandContext.trim() === '';
	const isButtonDisabled = isLastStep ? loadingCreateHome || isBrandDataEmpty : selectedFunnels.length === 0;

	return (
		<>
			<div className="fixed inset-0 z-40 overflow-hidden bg-white/90 backdrop-blur-md">
				<div className="fixed left-6 top-6 z-30 flex items-center gap-3 md:left-8 md:top-7">
					<button
						type="button"
						onClick={() => navigate('/funnels?type=homes')}
						aria-label="Volver"
						className="flex h-8 w-8 items-center justify-center text-slate-700 transition hover:text-fluxi"
					>
						<ArrowLeft className="h-5 w-5" strokeWidth={2} />
					</button>
					<img src={FluxiLogo} alt="Fluxi" className="h-7 w-auto -translate-y-0.5" />
				</div>

				<main className="flex h-full items-center justify-center px-4 pb-5 pt-20 md:px-8">
					<section className="flex h-[min(790px,calc(100vh-112px))] w-full max-w-6xl overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.12)]">
							<aside className="hidden w-[310px] flex-col gap-7 bg-[#F8FAFC] p-7 ring-1 ring-slate-100 lg:flex">
								<div>
									<p className="text-xs font-semibold uppercase tracking-[0.18em] text-fluxi">Tiendas</p>
								</div>

								<div className="space-y-3">
								{STEP_TITLES.map((title, index) => {
									const number = index + 1;
									const active = step === number;
									const done = step > number;
									return (
										<div
											key={title}
											className={`flex items-center gap-3 rounded-2xl p-3 transition ${
												active ? 'bg-white shadow-sm ring-1 ring-slate-200' : 'text-slate-500'
											}`}
										>
											<span className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold ${
												done || active ? 'bg-fluxi text-white' : 'bg-slate-200 text-slate-500'
											}`}>
												{number}
											</span>
											<span className="text-sm font-medium">{title}</span>
										</div>
									);
								})}
							</div>
						</aside>

						<div className="flex min-w-0 flex-1 flex-col">
							<header className="border-b border-slate-100 px-5 py-5 md:px-8">
								<div className="flex flex-wrap items-center justify-between gap-4">
									<div>
											<p className="text-xs font-semibold uppercase tracking-[0.18em] text-fluxi lg:hidden">Tiendas</p>
											<h2 className="text-xl font-semibold text-slate-950">{STEP_TITLES[step - 1]}</h2>
									</div>
									<div className="flex items-center gap-2">
										{STEP_TITLES.map((title, index) => (
											<span
												key={title}
												className={`h-2.5 rounded-full transition-all ${
													step === index + 1 ? 'w-9 bg-fluxi' : 'w-2.5 bg-slate-200'
												}`}
											/>
										))}
									</div>
								</div>
							</header>

							<div className="min-h-0 flex-1 overflow-y-auto px-5 py-6 md:px-8">
								{renderStepContent()}
							</div>

							<footer className="flex shrink-0 items-center justify-between border-t border-slate-100 px-5 py-5 md:px-8">
								{step > 1 ? (
									<button
										type="button"
										onClick={previousStep}
										className="flex items-center gap-2 px-5 py-3 text-sm font-medium text-slate-600 transition hover:text-slate-950"
									>
										<ArrowLeft className="h-5 w-5" />
										{dictionary['previous']?.[language as Language] ?? 'Anterior'}
									</button>
								) : (
									<div />
								)}

								<button
									type="button"
									onClick={isLastStep ? handleSubmit : handleNextStep}
									className={`ml-auto flex items-center gap-2 rounded-full px-7 py-3 text-sm font-medium text-white transition ${
										isButtonDisabled
											? 'cursor-not-allowed bg-slate-300'
											: isLastStep
												? 'bg-gradient-to-r from-fluxi via-[#2F6EDA] to-[#1D4ED8] shadow-lg shadow-blue-200 hover:from-[#2F6EDA] hover:to-[#2558B8]'
												: 'bg-fluxi hover:bg-fluxi-dark'
									}`}
									disabled={isButtonDisabled}
								>
									{isLastStep ? (
										loadingCreateHome ? <Loader /> : (dictionary['create-store']?.[language as Language] ?? 'Crear tienda')
									) : (
										<>
											{dictionary['next']?.[language as Language] ?? 'Siguiente'}
											<ArrowRight className="h-5 w-5" />
										</>
									)}
								</button>
							</footer>
						</div>
					</section>
				</main>
			</div>

			{isOpenBuyCreditsModal && (
				<ModalWrapper isOpen={isOpenBuyCreditsModal} onClose={() => setIsOpenBuyCreditsModal(false)}>
					<BuyCreditsModal
						onClose={() => setIsOpenBuyCreditsModal(false)}
						nextRenewalDate={endMembershipDate}
						language={language as Language}
						openInNewTab={true}
					/>
				</ModalWrapper>
			)}
		</>
	);
}
